/* ============================================================================
   wd-product.js — the product page: variant picker, gallery, add to bag,
   sticky bar. Loaded by sections/main-product only.

   The picker updates optimistically from the variants JSON the section
   prints, then asks the Section Rendering API for the same section at
   ?variant=<id> and swaps the price block from it — so the Omnibus logic in
   snippets/wd-price (which only Liquid can evaluate, because it reads a
   variant metafield) is never re-implemented in JavaScript.
   ========================================================================== */
(() => {
  'use strict';
  const WD = window.WD || {};
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => Array.from(r.querySelectorAll(s));

  const initProduct = (root) => {
    if (!root || root.dataset.wdInit) return;
    root.dataset.wdInit = '1';
    const form = $('.pdp__form', root);
    const dataEl = root.parentElement.querySelector('[data-variants]') || $('[data-variants]');
    if (!form || !dataEl) return;
    const variants = JSON.parse(dataEl.textContent);
    const idInput = $('[data-variant-id]', form);
    const section = root.dataset.section;
    const url = root.dataset.productUrl;
    const bar = $('[data-sticky-bar]');
    const addButtons = [...$$('[data-add-button]', root), ...(bar ? $$('[data-add-button]', bar) : [])];
    let current = variants.find((v) => String(v.id) === idInput.value) || variants[0];

    const selected = () => [1, 2].map((n) => $(`[data-option-input="${n}"]:checked`, form)?.value).filter((v) => v !== undefined);

    const markAvailability = () => {
      const [c1] = selected();
      // a case is unavailable if no available variant has this colour + that case
      $$('[data-option-input="2"]', form).forEach((inp) => {
        const ok = variants.some((v) => v.options[0] === c1 && v.options[1] === inp.value && v.available);
        inp.toggleAttribute('data-unavailable', !ok);
      });
      $$('[data-option-input="1"]', form).forEach((inp) => {
        const ok = variants.some((v) => v.options[0] === inp.value && v.available);
        inp.toggleAttribute('data-unavailable', !ok);
      });
    };

    const setLabel = (v) => {
      const label = !v ? WD.strings.unavailable
        : !v.available ? WD.strings.soldOut
        : root.dataset.preOrder === 'true' && v.inventory_quantity <= 0 ? WD.strings.preOrder
        : WD.strings.addToBag;
      addButtons.forEach((b) => {
        const l = b.querySelector('.btn__label');
        if (l) l.textContent = label;
        b.setAttribute('aria-disabled', String(!v || !v.available));
      });
    };

    const filterGallery = (colour) => {
      const slides = $$('[data-media-colour]', $('[data-gallery-stage]', root));
      const any = slides.some((s) => s.dataset.mediaColour === colour);
      slides.forEach((s) => { s.hidden = any && s.dataset.mediaColour !== '' && s.dataset.mediaColour !== colour; });
      $$('[data-thumb]', root).forEach((t) => { t.hidden = any && t.dataset.mediaColour !== '' && t.dataset.mediaColour !== colour; });
      const first = slides.find((s) => !s.hidden);
      const stage = $('[data-gallery-stage]', root);
      if (first && stage) stage.scrollTo({ left: 0, top: 0 });
      const firstThumb = $$('[data-thumb]', root).find((t) => !t.hidden);
      $$('[data-thumb]', root).forEach((t) => t.toggleAttribute('aria-current', t === firstThumb));
    };

    const refreshPrice = async (v) => {
      try {
        const res = await fetch(`${url}?variant=${v.id}&section_id=${section}`);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('[data-price-wrap]');
        const here = $('[data-price-wrap]', root);
        if (fresh && here) here.innerHTML = fresh.innerHTML;
      } catch (e) { /* the optimistic price stays */ }
    };

    const onChange = () => {
      const opts = selected();
      const v = variants.find((x) => x.options.every((o, i) => opts[i] === undefined || o === opts[i]));
      $$('[data-option-value]', form).forEach((el) => { el.textContent = opts[0]; });
      markAvailability();
      if (v && v.id !== current.id) {
        const colourChanged = v.options[0] !== current.options[0];
        current = v;
        idInput.value = v.id;
        const priceNow = $('.price__now', root);
        if (priceNow) priceNow.textContent = v.price_formatted;
        const u = new URL(window.location.href);
        u.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', u);
        if (colourChanged) filterGallery(v.options[0]);
        const bv = bar && $('[data-bar-variant]', bar);
        if (bv) bv.textContent = `${v.title} · ${v.price_formatted}`;
        refreshPrice(v);
      }
      setLabel(v);
    };
    form.addEventListener('change', (e) => { if (e.target.matches('[data-option-input]')) onChange(); });
    markAvailability();

    // quantity stepper
    root.addEventListener('click', (e) => {
      const step = e.target.closest('[data-qty-step]');
      if (!step) return;
      const input = step.parentElement.querySelector('input');
      const n = Math.min(Number(input.max) || 99, Math.max(Number(input.min) || 1, (Number(input.value) || 1) + Number(step.dataset.qtyStep)));
      input.value = n;
    });

    // add to bag
    form.addEventListener('submit', async (e) => {
      if (!WD.cart) return;
      e.preventDefault();
      if (!current.available) return;
      const err = $('[data-form-error]', form);
      err.hidden = true;
      addButtons.forEach((b) => b.setAttribute('aria-busy', 'true'));
      try {
        await WD.cart.add(new FormData(form));
        WD.openSheet('wd-cart', e.submitter) || WD.toast(WD.strings.added);
      } catch (ex) {
        err.textContent = ex.message; err.hidden = false;
      }
      addButtons.forEach((b) => b.removeAttribute('aria-busy'));
    });

    // gallery thumbs + scroll sync
    const stage = $('[data-gallery-stage]', root);
    root.addEventListener('click', (e) => {
      const t = e.target.closest('[data-thumb]');
      if (t) {
        const slide = $(`[data-media-id="${t.dataset.thumb}"]`, stage);
        if (slide) stage.scrollTo({ left: slide.offsetLeft - stage.offsetLeft, behavior: 'smooth' });
        $$('[data-thumb]', root).forEach((x) => x.toggleAttribute('aria-current', x === t));
        return;
      }
      const s = e.target.closest('.pdp__slide');
      if (s && window.matchMedia('(min-width: 1024px)').matches) {
        s.classList.toggle('is-zoomed');
        document.body.toggleAttribute('data-zoom', s.classList.contains('is-zoomed'));
      }
    });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') { $$('.pdp__slide.is-zoomed', root).forEach((s) => s.classList.remove('is-zoomed')); document.body.removeAttribute('data-zoom'); } });
    if (stage && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((ents) => ents.forEach((en) => {
        if (en.isIntersecting && en.intersectionRatio > 0.6) {
          const id = en.target.dataset.mediaId;
          $$('[data-thumb]', root).forEach((x) => x.toggleAttribute('aria-current', x.dataset.thumb === id));
        }
      }), { root: stage, threshold: [0.6] });
      $$('.pdp__slide', stage).forEach((s) => io.observe(s));
    }

    // sticky bar: shown on a phone once the real add button scrolls away
    const mainAdd = $('.pdp__add', root);
    if (bar && mainAdd && 'IntersectionObserver' in window) {
      bar.hidden = false;
      new IntersectionObserver(([en]) => bar.classList.toggle('is-on', !en.isIntersecting && en.boundingClientRect.top < 0)).observe(mainAdd);
    }
  };
  $$('[data-product]').forEach(initProduct);
  // Theme editor re-renders the section in place: bind the new markup.
  document.addEventListener('shopify:section:load', (e) => $$('[data-product]', e.target).forEach(initProduct));
})();
